import { getPrettyCarrierName } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { ShipmentMessage } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

export const ShipmentMessagesCard = memo(function ShipmentMessagesCard({
  fulfillmentGroups,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
}) {
  const groupedMessages = useMemo(() => {
    const messagesByCarrier: Record<string, ShipmentMessage[]> = {};
    const seen = new Set<string>();

    for (const group of fulfillmentGroups) {
      if (!group.shipmentMessages) {
        continue;
      }

      for (const messages of Object.values(group.shipmentMessages)) {
        for (const message of messages) {
          const carrier = message.carrier ?? "unknown";

          if (!messagesByCarrier[carrier]) {
            messagesByCarrier[carrier] = [];
          }

          const messageKey = `${carrier}-${message.message}-${message.type}`;

          if (!seen.has(messageKey) && !message.ignored) {
            seen.add(messageKey);
            messagesByCarrier[carrier].push(message);
          }
        }
      }
    }

    return messagesByCarrier;
  }, [fulfillmentGroups]);

  return (
    <Flex dir="column" gap="lg">
      <Flex dir="column" gap="xs">
        <Text color="secondary" fontSize="sm" fontWeight="semibold">
          Shipping messages
        </Text>
        <Text color="quaternary" fontSize="xxs">
          Errors or warnings generated while retrieving rates for this shipment
        </Text>
      </Flex>

      <Flex dir="column" gap="lg" pl="lg">
        {Object.entries(groupedMessages).map(([carrier, messages]) =>
          messages.length > 0 ? (
            <Flex dir="column" gap="lg" key={carrier}>
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {getPrettyCarrierName(carrier)}
              </Text>

              <Flex dir="column" gap="xxs" pl="lg">
                {messages
                  .filter(
                    (message) =>
                      message.message?.length && message.message.length > 0,
                  )
                  .map((message, index) => (
                    <Flex align="flex-start" dir="row" gap="sm" key={index}>
                      <Text fontSize="xs" textColor="secondary">
                        •
                      </Text>
                      <Flex dir="column" gap="xxs">
                        {message.message && (
                          <Text fontSize="xs" textColor="secondary">
                            {message.message}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  ))}
              </Flex>
            </Flex>
          ) : null,
        )}
      </Flex>
    </Flex>
  );
});
